import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { KeyFindingsEditor } from './KeyFindingsEditor';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { CheckCircle } from '@lifeomic/chromicons';

const messages = defineMessages({
  keyFindings: {
    id: 'detailView.keyFindings',
    defaultMessage: 'Key Findings',
  },
});

const useStyles = makeStyles<{ isEditing: boolean }>((theme: Theme) => ({
  findings: {
    maxHeight: 'fit-content',
    maxWidth: '65ch',
    '& ul': {
      columns: `2 ${theme.pxToRem(275)}`,
      columnGap: theme.spacing(4),
      paddingLeft: theme.spacing(1.25),
      margin: 0,
      '& li': {
        display: 'grid',
        gridTemplateColumns: `${theme.pxToRem(24)} 1fr`,
        padding: theme.spacing(0.5, 0),
        listStyleType: 'none',
        fontSize: theme.pxToRem(14),
        color: theme.palette.text.secondary,
        '& span': {
          display: 'inline-block',
          marginTop: theme.spacing(0.25),
        },
      },
    },
  },
  listIcon: {
    color: theme.palette.success.main,
    width: theme.pxToRem(18),
  },
}));

export type KeyFindingsProps = {
  isEditing: boolean;
  isDisabled: boolean;

  /** The current state of the findings when editing */
  editingState: string[];
  /** The saved state of the findings. Not required if editing auto-saves */
  savedState?: string[];
  onFindingsChange?: (findings: string[]) => void;
};

export const KeyFindings = ({
  editingState,
  savedState,
  isEditing,
  isDisabled,
  onFindingsChange,
}: KeyFindingsProps) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({ isEditing });

  const addFinding = (finding: string) => {
    onFindingsChange?.(editingState.concat(finding));
  };
  const deleteFinding = (finding: string) => {
    onFindingsChange?.(editingState.filter(f => f !== finding));
  };

  React.useEffect(() => {
    if (savedState) {
      onFindingsChange?.(editingState);
    }
  }, [editingState, savedState, onFindingsChange]);

  if (isEditing) {
    return (
      <KeyFindingsEditor
        onSelect={addFinding}
        onDelete={deleteFinding}
        findings={editingState}
        disabled={isDisabled}
      />
    );
  }
  return (
    <Box className={classes.findings} direction="column" gap={1}>
      <Text size="body" weight="bold">
        {formatMessage(messages.keyFindings)}
      </Text>
      <ul>
        {(savedState || editingState).map((finding, index) => (
          <li key={finding ?? index}>
            <CheckCircle className={classes.listIcon} />
            <span>{finding}</span>
          </li>
        ))}
      </ul>
    </Box>
  );
};
